<template>
    <div class="tickets-list-view">
        <MerusDataTable
            :headers="headers"
            :items="items"
            :loading="loading"
            :loading-text="$t('ticket.loading') || 'Carregando...'"
            :server-items-length="serverItemsLength"
            :items-per-page="itemsPerPage"
            :items-per-page-last-option="itemsPerPageLastOption"
            :server-sort-by="serverSortBy"
            :server-sort-desc="serverSortDesc"
            :item-key="itemKey"
            :append-actions="showPrint"
            @click:row="onRowClick"
            @update:options="$emit('update:options', $event)"
            @update:filters="$emit('update:filters', $event)"
        >
            <template v-slot:item.id_ticket="{ item }">
                <span class="font-weight-medium primary--text" style="overflow: break-word;">{{ item.cod_ticket || item.id_ticket }}</span>
            </template>
            <template v-slot:item.titulo="{ item }">
                <span class="text-truncate d-inline-block" style="max-width: 280px;overflow: break-word;" :title="item.titulo">{{ item.titulo || '–' }}</span>
            </template>
            <template v-slot:item.solicitacao_candidatos_unidade_descricao="{ item }">
                <span class="text-truncate d-inline-block" style="max-width: 220px;overflow: break-word;" :title="unidadesCell(item)">{{ unidadesCell(item) }}</span>
            </template>
            <template v-slot:item.solicitacao_candidatos_nome_agencia="{ item }">
                <span class="text-truncate d-inline-block" style="max-width: 180px;overflow: break-word;" :title="item.solicitacao_candidatos_nome_agencia">{{ item.solicitacao_candidatos_nome_agencia || '–' }}</span>
            </template>
            <template v-slot:item.tipo_solicitacao_extenso="{ item }">
                <span class="text-truncate d-inline-block" style="max-width: 200px;overflow: break-word;" :title="item.tipo_solicitacao_extenso">{{ item.tipo_solicitacao_extenso || '–' }}</span>
            </template>
            <template v-slot:item.status_atribuido_extenso="{ item }">
                {{ statusCellText(item) }}
            </template>
            <template v-slot:item.nome_usuario_atribuido="{ item }">
                {{ assigneesCell(item) }}
            </template>
            <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon
                            small
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            @click.stop="$emit('print-ticket', item)"
                        >
                            mdi-printer
                        </v-icon>
                    </template>
                    <span>{{ $t('ticket.print.printTicket') }}</span>
                </v-tooltip>
            </template>
            <template v-slot:item.sla_faixa="{ item }">
                <v-tooltip v-if="item.sla_faixa" bottom :disabled="!item.sla_prazo_extenso">
                    <template v-slot:activator="{ on, attrs }">
                        <v-chip
                            x-small
                            label
                            class="flex-shrink-0"
                            :color="slaCor(item)"
                            :text-color="isFinalizado(item) ? undefined : 'white'"
                            :outlined="isFinalizado(item)"
                            v-bind="attrs"
                            v-on="on"
                        >
                            <v-icon left x-small>{{ slaVencido(item) ? 'mdi-timer-alert' : 'mdi-timer-sand' }}</v-icon>
                            {{ item.sla_faixa === 'ATRASADO' ? $t('ticket.sla.late') : $t('ticket.sla.option', { n: parseInt(item.sla_faixa, 10) }) }}
                        </v-chip>
                    </template>
                    <span>
                        {{ $t('ticket.sla.deadline', { d: item.sla_prazo_extenso }) }}
                        <template v-if="slaVencido(item)"> · {{ $t('ticket.sla.overdue') }}</template>
                    </span>
                </v-tooltip>
            </template>
        </MerusDataTable>
    </div>
</template>

<script>
import MerusDataTable from '../Tables/MerusDataTable.vue';
import { ticketAssigneesDisplayLabel } from '@/utils/ticketAssignees';

/** Cor de cada faixa de SLA (Tickets.SQL_SLA_FAIXA); vencido sobrepõe a da faixa. */
const SLA_CORES = {
    '10H': 'success',
    '17H': 'success',
    '24H': 'warning',
    '48H': 'deep-orange',
    ATRASADO: 'error'
};

function slaVencido(t) {
    return !!t && (t.sla_vencido === true || Number(t.sla_vencido) === 1);
}

function slaCor(t) {
    if (!t) return 'grey';
    return slaVencido(t) ? 'error' : SLA_CORES[t.sla_faixa] || 'grey';
}

export default {
    name: 'TicketsListView',
    components: {
        MerusDataTable
    },
    props: {
        tickets: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        /** Paginação server-side (getticketsquadro modo lista): total filtrado e estado da página. */
        serverItemsLength: {
            type: Number,
            default: 0
        },
        itemsPerPage: {
            type: Number,
            default: 20
        },
        itemsPerPageLastOption: {
            type: Number,
            default: 1000
        },
        serverSortBy: {
            type: Array,
            default: () => []
        },
        serverSortDesc: {
            type: Array,
            default: () => []
        },
        /** Dispatcher Jira: show status dispatcher first so it matches the detail editor and board. */
        statusPreferDispatcher: {
            type: Boolean,
            default: false
        },
        /** Adds the "Ações" column with the print icon (parent opens the print preview). */
        showPrint: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        items() {
            return this.tickets || [];
        },
        headers() {
            return [
                { text: this.$t('ticket.headers.id'), value: 'id_ticket', filtrable: true, sortable: true, width: '80px' },
                { text: this.$t('ticket.headers.title'), value: 'titulo', sortable: true, filtrable: true },
                { text: this.$t('ticket.headers.unidades'), value: 'solicitacao_candidatos_unidade_descricao', sortable: true, filtrable: true },
                { text: this.$t('ticket.headers.areas'), value: 'solicitacao_candidatos_areas_label', sortable: false, filtrable: false },
                { text: this.$t('ticket.headers.agencia'), value: 'solicitacao_candidatos_nome_agencia', sortable: true, filtrable: true },
                { text: this.$t('ticket.headers.tipoSolicitacao'), value: 'tipo_solicitacao_extenso', sortable: true, filtrable: true },
                { text: this.$t('ticket.headers.statusAtribuido'), value: 'status_atribuido_extenso', sortable: false, filtrable: true },
                { text: this.$t('ticket.headers.usuario'), value: 'nome_usuario_atribuido', sortable: true, filtrable: true },
                { text: this.$t('ticket.headers.inclusionDate'), value: 'dt_inclusao_extenso', sortable: true, filtrable: true },
                { text: this.$t('ticket.headers.sla'), value: 'sla_faixa', sortable: false, filtrable: false }
            ];
        },
        itemKey() {
            return 'id_ticket';
        }
    },
    methods: {
        slaCor,
        slaVencido,
        isFinalizado(item) {
            return item.finalizado === 1 || item.finalizado === '1' || item.finalizado_extenso === 'Sim';
        },
        statusCellText(item) {
            if (this.statusPreferDispatcher) {
                return item.status_dispatcher_extenso || item.status_atribuido_extenso || item.status_extenso || '–';
            }
            return item.status_atribuido_extenso || item.status_dispatcher_extenso || item.status_extenso || '–';
        },
        assigneesCell(item) {
            const s = ticketAssigneesDisplayLabel(item);
            return s || '–';
        },
        unidadesCell(item) {
            const l = item.solicitacao_candidatos_unidade_label;
            if (l != null && String(l).trim() !== '') {
                return String(l).trim();
            }
            const d = item.solicitacao_candidatos_unidade_descricao;
            if (d != null && String(d).trim() !== '') {
                return String(d).trim();
            }
            const c = item.solicitacao_candidatos_cod_unidade;
            if (c != null && String(c).trim() !== '') {
                return String(c).trim();
            }
            return '–';
        },
        onRowClick(event, row) {
            const item = row && row.item !== undefined ? row.item : row;
            this.$emit('row-click', item);
        }
    }
};
</script>

<style scoped>
.tickets-list-view {
    flex: 1;
    min-height: 0;
    overflow: auto;
}
.tickets-list-view >>> tbody tr {
    cursor: pointer;
}
</style>
